'use client'

import tunnel from 'tunnel-rat'
import { create } from 'zustand'

import type {
  HomeFilterCategory,
  HomeFilters,
  PageSlug,
  PageType,
} from '@/constant/types'
import {
  areFiltersAreActive,
  getPrimaryFilter,
  INITIAL_HOME_FILTERS,
  toggleFilter,
} from '@/lib/homeFilters'

interface GlobalStoreState {
  windowWidth: number
  windowHeight: number
  isDesktopWidth: boolean | undefined
  isMobile: boolean
  isDevice: boolean
  pathname: string
  currentPageSlug: PageSlug | ''
  previousPageSlug: PageSlug | ''
  currentPageType: PageType | ''
  previousPageType: PageType | ''
  // SITE SPECIFIC
  isAlbumPage: boolean
  isMenuOpen: boolean
  isSecondaryMenuOpen: boolean
  secondaryButtonAction: (() => void) | null
  secondaryButtonLabel: string
  tunnel: ReturnType<typeof tunnel>
  homeFilters: HomeFilters
  areHomeFiltersActive: boolean
  showHomeFilters: boolean
  showDiaryIndex: boolean
  showChinaIndex: boolean
}

interface GlobalStoreActions {
  setIsMobile: (newState: boolean) => void
  setIsDevice: (newState: boolean) => void
  setWindowDimensions: (width: number, height: number) => void
  setPathname: (pathname: string) => void
  setCurrentPage: (pageSlug: PageSlug, pageType: PageType) => void
  // SITE SPECIFIC
  toggleMenuOpen: (state?: boolean) => void
  setSecondaryButtonAction: (action: () => void, label: string) => void
  clearSecondaryButtonAction: () => void
  setHomeFilters: ({
    type,
    value,
    action,
  }: {
    type: HomeFilterCategory
    value: string
    action: 'add' | 'remove'
  }) => void
  clearHomeFilters: () => void
  toggleHomeFilters: (state?: boolean) => void
  toggleDiaryIndex: (state?: boolean) => void
  toggleChinaIndex: (state?: boolean) => void
}

const INITIAL_STATE: GlobalStoreState = {
  windowWidth: 0,
  windowHeight: 0,
  isDesktopWidth: undefined,
  isMobile: true,
  isDevice: false,
  pathname: '',
  currentPageSlug: '',
  previousPageSlug: '',
  currentPageType: '',
  previousPageType: '',
  // SITE SPECIFIC
  isAlbumPage: false,
  isMenuOpen: false,
  isSecondaryMenuOpen: false,
  secondaryButtonAction: null,
  secondaryButtonLabel: '',
  tunnel: tunnel(),
  homeFilters: INITIAL_HOME_FILTERS,
  showHomeFilters: false,
  areHomeFiltersActive: false,
  showDiaryIndex: false,
  showChinaIndex: false,
}

const useGlobalState = create<
  GlobalStoreState & { actions: GlobalStoreActions }
>((set, get) => ({
  ...INITIAL_STATE,
  actions: {
    setIsMobile: (newState) => set({ isMobile: newState }),
    setIsDevice: (newState) => set({ isDevice: newState }),
    setWindowDimensions: (width, height) =>
      set({ windowWidth: width, windowHeight: height }),
    setPathname: (pathname: string) => set({ pathname }),
    setCurrentPage: (pageSlug: PageSlug, pageType: PageType) => {
      const { currentPageSlug, currentPageType } = get()
      const isAlbumPage = pageType === 'home' || pageType === 'album'
      return set(() => ({
        currentPageSlug: pageSlug,
        currentPageType: pageType,
        previousPageSlug: currentPageSlug,
        previousPageType: currentPageType,
        isAlbumPage,
      }))
    },
    // SITE SPECIFIC
    toggleMenuOpen: (state?: boolean) => {
      const newState = state === undefined ? !get().isMenuOpen : state
      set({
        isMenuOpen: newState,
        isSecondaryMenuOpen: false,
        // Close secondary menu if open
        showHomeFilters: false,
        showDiaryIndex: false,
        showChinaIndex: false,
      })
    },
    setSecondaryButtonAction: (action, label) =>
      set({ secondaryButtonAction: action, secondaryButtonLabel: label }),
    clearSecondaryButtonAction: () =>
      set({
        secondaryButtonAction: null,
        secondaryButtonLabel: '',
        isSecondaryMenuOpen: false,
      }),
    setHomeFilters: ({ type, value, action }) => {
      const { homeFilters } = get()
      const newState = toggleFilter({ state: homeFilters, type, value, action })
      const areActive = areFiltersAreActive(newState)
      // Set primary filter...
      newState.primaryFilter = getPrimaryFilter(homeFilters, newState)
      // Update state
      set({
        homeFilters: newState,
        areHomeFiltersActive: areActive,
      })
    },
    clearHomeFilters: () => {
      const newState = INITIAL_HOME_FILTERS
      set({
        homeFilters: newState,
        areHomeFiltersActive: false,
      })
    },
    toggleHomeFilters: (state?: boolean) => {
      const newState = state === undefined ? !get().showHomeFilters : state
      set({
        showHomeFilters: newState,
        isSecondaryMenuOpen: newState,
        isMenuOpen: false,
      })
    },
    toggleDiaryIndex: (state?: boolean) => {
      const newState = state === undefined ? !get().showDiaryIndex : state
      set({
        showDiaryIndex: newState,
        isSecondaryMenuOpen: newState,
        isMenuOpen: false,
      })
    },
    toggleChinaIndex: (state?: boolean) => {
      const newState = state === undefined ? !get().showChinaIndex : state
      set({
        showChinaIndex: newState,
        isSecondaryMenuOpen: newState,
        isMenuOpen: false,
      })
    },
  },
}))

// * EXPORTS

export const useWindowWidth = () => useGlobalState((state) => state.windowWidth)
export const useWindowHeight = () =>
  useGlobalState((state) => state.windowHeight)

export const useIsDesktopWidth = () =>
  useGlobalState((state) => state.isDesktopWidth)

export const useIsMobile = () => useGlobalState((state) => state.isMobile)
export const useIsDevice = () => useGlobalState((state) => state.isDevice)

export const usePathname = () => useGlobalState((state) => state.pathname)
export const useCurrentPageSlug = () =>
  useGlobalState((state) => state.currentPageSlug)
export const usePreviousPageSlug = () =>
  useGlobalState((state) => state.previousPageSlug)
export const useCurrentPageType = () =>
  useGlobalState((state) => state.currentPageType)
export const usePreviousPageType = () =>
  useGlobalState((state) => state.previousPageType)

export const useIsAlbumPage = () => useGlobalState((state) => state.isAlbumPage)

export const useIsMenuOpen = () => useGlobalState((state) => state.isMenuOpen)
export const useIsSecondaryMenuOpen = () =>
  useGlobalState((state) => state.isSecondaryMenuOpen)

export const useSecondaryButtonAction = () =>
  useGlobalState((state) => state.secondaryButtonAction)
export const useSecondaryButtonLabel = () =>
  useGlobalState((state) => state.secondaryButtonLabel)

export const useTunnel = () => useGlobalState((state) => state.tunnel)

export const useHomeFilters = () => useGlobalState((state) => state.homeFilters)
export const useAreHomeFiltersActive = () =>
  useGlobalState((state) => state.areHomeFiltersActive)
export const useShowHomeFilters = () =>
  useGlobalState((state) => state.showHomeFilters)

export const useShowDiaryIndex = () =>
  useGlobalState((state) => state.showDiaryIndex)

export const useShowChinaIndex = () =>
  useGlobalState((state) => state.showChinaIndex)

export const useGlobalStateActions = () =>
  useGlobalState((state) => state.actions)

export default useGlobalState
